/**
 * PERSONAL AIRCRAFT — the machine a pilot owns, and what he paints on it.
 *
 * Split out of `career-surface.ts` when G41 gave BACKUP CAREER a screen, for
 * the same reason `career-pilot-screens.ts` was split out at G31 and
 * `consequence-dialog.ts` at B5: that file's job is to say which screen is on,
 * and every subject that grows its own pair of store writes wants to be a
 * module before it is a method. The three things here are one subject — what
 * the turntable is showing, and the two commands that change it.
 *
 * These touch the store, so they are here rather than under `shell/model/`,
 * beside `career-store.ts` whose writes they are.
 */

import { bakedModelIdOf, paintAircraft, personalAircraftRequest, requestPersonalAircraft, type PaintColor } from "@red-opus/engine";
import type { PersonalPaint } from "@red-opus/formats";
import { careerPaint } from "./career-paint.ts";
import { saveCareer, type CareerSave, type CareerStore } from "./career-store.ts";
import type { PreviewPose } from "./preview-pose.ts";

/** Which body part `PAINT WHAT?` picked. Mirrors `shell/model/paint-screens.ts`'s own union without importing the shell from the career layer. */
export type AircraftPaintPart = "wings" | "fuselage" | "everything";

export interface PreviewSubject {
  readonly modelId: string;
  readonly paint: PersonalPaint | undefined;
  readonly pose: PreviewPose;
}

/**
 * What the turntable should be showing, or `undefined` when there is nothing to
 * show — no pilot, no machine of his own, or a screen that has no turntable.
 */
export function previewSubjectFor(save: CareerSave | undefined, showing: boolean, pose: PreviewPose): PreviewSubject | undefined {
  const held = save?.career.personalAircraft;
  if (!save || !held || !showing) return undefined;
  const modelId = bakedModelIdOf(held.aircraft);
  if (!modelId) return undefined;
  return { modelId, paint: careerPaint(save.career), pose };
}

/**
 * REQUEST NEW AIRCRAFT → the list → the machine is his. Returns whether the
 * choice was a real one, so the caller knows whether to move the screen.
 */
export function takePersonalAircraft(store: CareerStore, save: CareerSave, index: number): boolean {
  const chosen = personalAircraftRequest(save.career).offered[index];
  if (!chosen) return false;
  saveCareer(store, requestPersonalAircraft(save.career, chosen));
  return true;
}

/**
 * `WHICH COLOR?` answered. `everything` is the original's paint-both shortcut,
 * not a third body part — the manual is explicit that there are two sections,
 * wings and fuselage.
 */
export function paintPersonalAircraft(store: CareerStore, save: CareerSave, part: AircraftPaintPart, color: PaintColor): void {
  const choice = part === "everything" ? { wings: color, fuselage: color } : { [part]: color };
  saveCareer(store, paintAircraft(save.career, choice));
}
